import type {IOrderRepository} from '../../../domain/repositories/IOrderRepository';
import type {IProductRepository} from '../../../domain/repositories/IProductRepository';
import type {IUserRepository} from '../../../domain/repositories/IUserRepository';
import {IPaymentService, PaymentFrequency} from '../../../domain/services/IPaymentService';
import {Order, OrderCreateParams, PaymentType} from '../../../domain/entities/Order';

export class CreateOrderUseCase {
    constructor(
        private readonly orderRepository: IOrderRepository,
        private readonly productRepository: IProductRepository,
        private readonly userRepository: IUserRepository,
        private readonly paymentService: IPaymentService
    ) {
    }

    async execute(params: CreateOrderParams): Promise<CreateOrderResult> {
        // Validate product exists and is available
        const product = await this.productRepository.findById(params.productId);
        if (!product || !product.isAvailable()) {
            throw new Error('Product is not available');
        }

        // Validate user exists
        const user = await this.userRepository.findById(params.userId);
        if (!user) {
            throw new Error('User not found');
        }

        // Validate payment type and user eligibility
        if (params.paymentType === PaymentType.INSTALLMENT || params.paymentType === PaymentType.PAY_LATER) {
            const runtimePrice = Number(
                product.pricing?.current_display_price ||
                product.pricing?.display_price ||
                product.retail_price ||
                0
            );
            const effectiveProductPrice =
                Number.isFinite(runtimePrice) && runtimePrice > 0
                    ? runtimePrice
                    : product.retail_price;

            if (!user.isEligibleForCredit()) {
                throw new Error('User is not eligible for credit-based payments');
            }

            if (!Number.isFinite(effectiveProductPrice) || effectiveProductPrice <= 0) {
                throw new Error('Product price is unavailable');
            }

            if (!user.canMakePurchase(effectiveProductPrice)) {
                throw new Error('Purchase amount exceeds user spending limit');
            }

            if (!product.isEligibleForPaymentPlan()) {
                throw new Error('Product is not eligible for payment plans');
            }
        }

        const runtimePrice = Number(
            product.pricing?.current_display_price ||
            product.pricing?.display_price ||
            product.retail_price ||
            0
        );
        const effectiveProductPrice =
            Number.isFinite(runtimePrice) && runtimePrice > 0
                ? runtimePrice
                : product.retail_price;

        // Calculate payment plan if needed
        let paymentPlan: any[] = [];
        if (params.paymentType === PaymentType.INSTALLMENT) {
            const planCalculation = this.paymentService.calculatePaymentPlan(
                effectiveProductPrice,
                params.planFrequency! as PaymentFrequency,
                params.planDuration!
            );

            paymentPlan = planCalculation.installments.map((installment, index) => ({
                id: `${params.orderId}_${index + 1}`,
                orderId: params.orderId,
                amount: installment.amount,
                dueDate: installment.dueDate,
                status: 'pending'
            }));
        }

        // Create order
        const orderParams: OrderCreateParams = {
            id: params.orderId,
            userId: params.userId,
            productId: params.productId,
            productName: product.name,
            productPrice: effectiveProductPrice,
            productAdjustedPrice: product.cost_price,
            productTotalPrice: effectiveProductPrice,
            imageUrl: product.images && product.images.length > 0 ? product.images[0]?.image || null : null,
            merchantName: product.shop_name,
            shopName: product.shop_name,
            paymentType: params.paymentType,
            planDuration: params.planDuration || 1,
            planFrequency: params.planFrequency || 'monthly',
            phoneNumber: user.phoneNumber,
            platform: params.platform || 'web',
            userName: user.name,
            createdBy: params.userId,
            paymentPlan: paymentPlan,
            keepMyPrice: params.keepMyPrice || false,
            keepMyPriceFee: params.keepMyPriceFee || 0
        };

        const order = await this.orderRepository.create(orderParams);

        return {
            order: order,
            paymentRequired: params.paymentType === PaymentType.FULL_PAYMENT,
            paymentAmount: params.paymentType === PaymentType.FULL_PAYMENT
                ? effectiveProductPrice
                : paymentPlan[0]?.amount || 0
        };
    }
}

export interface CreateOrderParams {
    orderId: string;
    userId: string;
    productId: string;
    paymentType: PaymentType;
    planDuration?: number;
    planFrequency?: string;
    platform?: string;
    keepMyPrice?: boolean;
    keepMyPriceFee?: number;
}

export interface CreateOrderResult {
    order: Order;
    paymentRequired: boolean;
    paymentAmount: number;
}
